import type { UseTuningSession } from '../hooks/useTuningSession';
import { Card } from '@/components/ui/Card';
import { GoalCard, TipCard, WarningCard, InfoCard } from '../components/Cards';
import { CheatSheetButton } from '../components/CheatSheets';
import { SafetyNote } from '@/components/SafetyNote';
import { SAFETY_NOTES } from '@/lib/disclaimers';

type Direction = 'same' | 'high' | 'low' | 'left' | 'right';

interface BroadheadData {
  distance?: number;
  broadheadType?: 'fixed' | 'mechanical' | 'hybrid';
  bhDirection?: Direction;
  spinTested?: boolean;
}

interface Props { tuning: UseTuningSession }

/**
 * Step 10 — Broadhead Group
 *
 * The exam. Fixed-blade broadheads expose every flaw in tuning that field
 * points hide. If BH and FP hit the same group at 30+ yards, the bow is fully
 * tuned for hunting.
 */
export function BroadheadGroupStep({ tuning }: Props) {
  const data = (tuning.session.stepData['broadhead_group'] as BroadheadData | undefined) ?? {};
  const update = (patch: Partial<BroadheadData>) =>
    tuning.updateStepData<BroadheadData>('broadhead_group', { ...data, ...patch });

  return (
    <>
      <SafetyNote severity={SAFETY_NOTES.broadhead.severity} title={SAFETY_NOTES.broadhead.title}>
        {SAFETY_NOTES.broadhead.body}
      </SafetyNote>

      <GoalCard title="What we're trying to achieve">
        Fixed-blade broadheads shot at the same aim point as field points should land in
        the SAME group at 30-40 yards. If they don't, your tune isn't fully resolved —
        a small flaw that a field point forgives shows up as a 6" gap with broadheads.
      </GoalCard>

      <Card padding="lg">
        <div className="flex items-baseline justify-between mb-3 gap-2 flex-wrap">
          <h3 className="font-semibold">Setup</h3>
          <CheatSheetButton defaultSheet="poi-tuning" label="POI tuning guide" />
        </div>
        <ol className="list-decimal list-inside space-y-1 text-[13px] text-zinc-300 leading-relaxed">
          <li>Use the SAME total point weight for field points and broadheads (within ±5gr).</li>
          <li>Spin-test every broadhead arrow before shooting — wobbly = bent insert or BH ferrule.</li>
          <li>Stand at 30-40 yards. Shoot 3 field-point arrows at the bullseye — note group center.</li>
          <li>Shoot 3 broadhead arrows at the SAME bullseye — compare BH group center.</li>
        </ol>

        <div className="grid sm:grid-cols-2 gap-3 mt-4">
          <Field label="Distance (yards)">
            <input
              type="number"
              min={20}
              max={80}
              step={5}
              value={data.distance ?? ''}
              onChange={(e) => update({ distance: parseFloatOrUndef(e.target.value) })}
              placeholder="30-40"
              className={inputClass}
            />
          </Field>
          <Field label="Broadhead type">
            <select
              value={data.broadheadType ?? ''}
              onChange={(e) =>
                update({ broadheadType: (e.target.value as BroadheadData['broadheadType']) || undefined })
              }
              className={inputClass}
            >
              <option value="">Select…</option>
              <option value="fixed">Fixed blade (truest tune test)</option>
              <option value="mechanical">Mechanical / expandable</option>
              <option value="hybrid">Hybrid (1 fixed + 2 mech)</option>
            </select>
          </Field>
        </div>

        <label className="flex items-start gap-2 cursor-pointer mt-3">
          <input
            type="checkbox"
            checked={!!data.spinTested}
            onChange={(e) => update({ spinTested: e.target.checked })}
            className="mt-1 w-4 h-4 accent-ember-500"
          />
          <span className="text-sm">
            I spin-tested each broadhead arrow on a spin tester — no wobble at the BH tip
          </span>
        </label>
      </Card>

      <Card padding="lg">
        <h3 className="font-semibold mb-3">Where do your broadheads group relative to field points?</h3>
        <div className="grid sm:grid-cols-2 gap-2">
          <Choice label="✓ Same group — BH and FP overlap" selected={data.bhDirection === 'same'} onClick={() => update({ bhDirection: 'same' })} />
          <Choice label="↑ BH HIGH of field points" selected={data.bhDirection === 'high'} onClick={() => update({ bhDirection: 'high' })} />
          <Choice label="↓ BH LOW of field points" selected={data.bhDirection === 'low'} onClick={() => update({ bhDirection: 'low' })} />
          <Choice label="← BH LEFT of field points (RH)" selected={data.bhDirection === 'left'} onClick={() => update({ bhDirection: 'left' })} />
          <Choice label="→ BH RIGHT of field points (RH)" selected={data.bhDirection === 'right'} onClick={() => update({ bhDirection: 'right' })} />
        </div>
      </Card>

      {data.bhDirection === 'same' && (
        <div className="rounded-lg border border-emerald-700/40 bg-emerald-950/20 p-3 flex gap-2.5">
          <div className="text-emerald-400 text-2xl">★</div>
          <div className="text-sm">
            <p className="font-semibold text-emerald-300">Your bow is fully tuned.</p>
            <p className="text-zinc-300 mt-0.5">
              Fixed-blade broadheads + field points hitting the same group at distance is the
              gold standard. You're hunt-ready. Generate your final report.
            </p>
          </div>
        </div>
      )}

      {data.bhDirection && data.bhDirection !== 'same' && (
        <TipCard title="Broadheads aren't grouping with field points — what to do" defaultOpen>
          <p className="mb-2">
            The fixes are the same as bareshaft tuning — broadheads expose the same dynamic
            tune issues a bareshaft does, just at full hunting distance. From the POI tuning
            guide:
          </p>
          {data.bhDirection === 'high' && (
            <ol className="list-decimal list-inside space-y-1 text-zinc-300">
              <li>Move the nocking point UP, or move the rest DOWN.</li>
              <li>Advance the TOP cam (twist top cable end); delay the BOTTOM cam.</li>
              <li>Re-check bareshaft group at 20 yards before retesting BH at 40.</li>
            </ol>
          )}
          {data.bhDirection === 'low' && (
            <ol className="list-decimal list-inside space-y-1 text-zinc-300">
              <li>Move the nocking point DOWN, or move the rest UP.</li>
              <li>Advance the BOTTOM cam (twist bottom cable end); delay the TOP cam.</li>
              <li>Re-check bareshaft at 20 yards before retesting BH.</li>
            </ol>
          )}
          {data.bhDirection === 'left' && (
            <ol className="list-decimal list-inside space-y-1 text-zinc-300">
              <li>Move the cam RIGHT, OR twist the RIGHT yoke / untwist the LEFT.</li>
              <li>Move the rest LEFT (toward riser).</li>
              <li>If persistent: BH ferrule may be misaligned — try a different BH arrow.</li>
            </ol>
          )}
          {data.bhDirection === 'right' && (
            <ol className="list-decimal list-inside space-y-1 text-zinc-300">
              <li>Move the cam LEFT, OR twist the LEFT yoke / untwist the RIGHT.</li>
              <li>Move the rest RIGHT (away from riser).</li>
              <li>If persistent: spine may be too weak — common with heavy BH.</li>
            </ol>
          )}
        </TipCard>
      )}

      {data.broadheadType === 'mechanical' && data.bhDirection !== 'same' && (
        <TipCard title="Mechanical broadhead caveat">
          <p>
            Mechanicals fly closer to field points than fixed-blades and are LESS sensitive
            to small tune flaws. If your mechanicals miss field-point POI, your tune is
            unusually off — re-check center shot + cam timing.
          </p>
        </TipCard>
      )}

      {!data.spinTested && (
        <WarningCard title="Spin-test every broadhead before shooting">
          <p className="mb-1">
            A bent BH ferrule OR a slightly off-axis insert will cause the BH arrow to
            tumble in flight — your group will look "out of tune" when really it's just
            mechanically broken.
          </p>
          <p className="text-[11px] text-zinc-400">
            $20 spin tester is the cheapest insurance in archery. Spin every arrow before
            it ever flies.
          </p>
        </WarningCard>
      )}

      <InfoCard title="Why fixed blades expose flaws">
        <p>
          Fixed-blade broadheads have wide stationary blades that act like wings during
          flight. Any small yaw or pitch coming off the rest is amplified by the blades
          into a wider drift. Field points have no wings, so they "smooth over" minor
          tune flaws. If fixed-BH and FP group together, your tune has zero residual
          dynamic error.
        </p>
      </InfoCard>

      <WarningCard title="Safety reminder">
        Broadheads are SHARP. Use a high-density broadhead-rated target (not a standard
        bag target — they'll destroy it AND your arrows). Keep blade sheaths on until you
        nock. Inspect blades after every shot for bending; bent blades fly wild.
      </WarningCard>
    </>
  );
}

const inputClass =
  'w-full bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-ember-500';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <div><label className="text-[11px] text-zinc-500 block mb-1">{label}</label>{children}</div>;
}

function Choice({ label, selected, onClick }: { label: string; selected: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={
        'text-left rounded-md border px-3 py-2.5 text-sm transition ' +
        (selected
          ? 'border-ember-500 bg-ember-500/10 text-ember-500 font-semibold'
          : 'border-zinc-800 bg-zinc-950 text-zinc-300 hover:border-zinc-700')
      }
    >
      {label}
    </button>
  );
}

function parseFloatOrUndef(s: string): number | undefined {
  if (s === '') return undefined;
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : undefined;
}
